import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { RichMediaInput } from '@bonusly/common-action-input';
import { RichTextInput } from '@bonusly/common-rich-text-editor';
import {
	Box,
	Button,
	ChatAltSolid,
	Checkbox,
	Flex,
	FormControl,
	FormLabel,
	HStack,
	InformationCircleOutline,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	PencilAltOutline,
	rem,
	Spacer,
	Stack,
	Text,
	Tooltip,
	VStack,
} from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { useModSubmit } from '../../hooks/use-mod-submit';
import { User } from '../../lib';
import { ReferenceEntity } from '../../lib/entities/reference';
import { ReferencePreview } from '../reference';
import { AiPromptNote } from './ai-prompt-note';
import { useRequestModal } from './hooks/use-request-modal';

interface RequestFeedbackProps {
	defaultText?: string;
	defaultUsers?: User[];
	isOpen: boolean;
	onClose: VoidFunction;
	reference?: ReferenceEntity;
}

export const RequestFeedbackModal: FC<RequestFeedbackProps> = ({ defaultText, defaultUsers, onClose, isOpen, reference }) => {
	const { t } = useTranslation('feedback');
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	const { track } = useAnalytics();
	useEffect(() => {
		// Ensure we only send an event when a user clicks to open the modal
		// and not on re-renders of the same modal being opened
		if (isOpen == true) {
			track(AnalyticsEvent.Feedback.RequestFeedbackModalOpened);
		}
	}, [track, isOpen]);

	const {
		isSubmitting,
		isValid,
		isVisibilityChecked,
		onClearGeneratedText,
		onRemoveReference,
		onReset,
		onSubmitDraft,
		onSubmitRequest,
		onToggleVisibility,
		recipientEditor,
		showAiAlert,
		showReference,
		textEditor,
		uniqueId,
		showShareWithManager,
	} = useRequestModal({
		defaultText,
		defaultUsers,
		isOpen,
		onSuccess: onClose,
		reference,
	});
	const form = useModSubmit(onSubmitRequest);
	const user = useCurrentUser();
	const { data: manager } = useUser(user.manager_id);

	return (
		<Modal
			autoFocus={false}
			isOpen={isOpen}
			onClose={onClose}
			onCloseComplete={onReset}
			scrollBehavior="inside"
			size={isHomepageRedesignEnabled ? { base: 'full', md: 'xl' } : '2xl'}
		>
			<ModalOverlay />
			<ModalContent as="form" ref={form}>
				<ModalHeader
					{...(!isHomepageRedesignEnabled && { borderBottom: '1px solid', borderColor: 'border.default' })}
					{...(isHomepageRedesignEnabled && { px: { base: rem(16), md: rem(24) }, pt: rem(20), pb: rem(12), borderBottom: 0 })}
				>
					<HStack justify="space-between" w="100%">
						<Text color="text.default" fontSize={isHomepageRedesignEnabled ? 'xl' : 'lg'} fontWeight="semibold" lineHeight={8}>
							{t('feedback.request.modal_header')}
						</Text>
						<ModalCloseButton h={rem(32)} w={rem(32)} pos="static" borderRadius="full" />
					</HStack>
				</ModalHeader>

				<ModalBody
					{...(!isHomepageRedesignEnabled && { px: 5, pt: 5, pb: 0 })}
					{...(isHomepageRedesignEnabled && { px: { base: rem(16), md: rem(24) }, py: 0 })}
				>
					<Stack spacing={4}>
						<Flex bg="hashtag.bgAlpha" p="2" borderRadius="sm" gap="2">
							<ChatAltSolid color="hashtag.fgHover" />
							<Text size="sm" pl="3">
								{t('feedback.request.privacy_notice')}
							</Text>
						</Flex>

						<FormControl>
							<FormLabel>{t('feedback.request.input_label')}</FormLabel>
							<RichTextInput editor={textEditor} fontSize="lg">
								{showAiAlert && <AiPromptNote onClickClear={onClearGeneratedText} />}
							</RichTextInput>
						</FormControl>

						<FormControl>
							<FormLabel>{t('feedback.request.user_label')}</FormLabel>
							<RichMediaInput border="1px solid" borderColor="inherit" borderRadius="md" editor={recipientEditor} fontSize="lg" uniqueId={uniqueId} />
						</FormControl>

						{showReference && <ReferencePreview reference={reference} onRemove={onRemoveReference} />}
					</Stack>
				</ModalBody>

				<ModalFooter>
					{isHomepageRedesignEnabled ? (
						<VStack w="100%" spacing={3}>
							<FormControl w="full" alignSelf="flex-start">
								<Checkbox checked={isVisibilityChecked} fontWeight="medium" onChange={onToggleVisibility} hidden={!showShareWithManager}>
									<HStack spacing={1} alignItems="center">
										<Text color="text.softest" fontWeight="normal" fontSize={50}>
											{t('feedback.request.share_with_manager', {
												name: manager?.displayFirstName,
											})}
										</Text>
										<Tooltip label={t('feedback.request.share_with_manager_tooltip')} placement="top">
											<Flex alignItems="center">
												<InformationCircleOutline color="text.softest" size="sm" />
											</Flex>
										</Tooltip>
									</HStack>
								</Checkbox>
							</FormControl>
							<HStack justify="space-between" w="100%">
								<Button
									colorScheme="secondary"
									variant="alpha"
									isDisabled={!isValid}
									isLoading={isSubmitting}
									onClick={onSubmitDraft}
									w="full"
									leftIcon={<PencilAltOutline size="sm" color={!isValid ? 'text.disabled' : 'secondary.fg'} />}
								>
									{t('feedback.request.save_as_draft')}
								</Button>
								<Button colorScheme="primary" isDisabled={!isValid} isLoading={isSubmitting} onClick={onSubmitRequest} w="full">
									{t('feedback.request.submit_button')}
								</Button>
							</HStack>
						</VStack>
					) : (
						<>
							<FormControl width="auto" maxWidth="300px">
								<Checkbox checked={isVisibilityChecked} fontWeight="medium" onChange={onToggleVisibility} hidden={!showShareWithManager}>
									<Flex h={rem(20)} fontSize="sm" color="text.softest" fontWeight="normal">
										{t('feedback.request.share_with_manager', {
											name: manager?.displayFirstName,
										})}
										<Tooltip label={t('feedback.request.share_with_manager_tooltip')}>
											<Box as="span" color="text.softest" display="inline-block" ml={1}>
												<InformationCircleOutline />
											</Box>
										</Tooltip>
									</Flex>
								</Checkbox>
							</FormControl>

							<Spacer />

							<Button colorScheme="secondary" mr={3} variant="alpha" isDisabled={!isValid} isLoading={isSubmitting} onClick={onSubmitDraft} p={4}>
								{t('feedback.request.save_as_draft')}
							</Button>

							<Button colorScheme="primary" isDisabled={!isValid} isLoading={isSubmitting} onClick={onSubmitRequest} p={4}>
								{t('feedback.request.submit_button')}
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
